<script lang="ts">
	import { onMount, tick } from 'svelte'
	import type { KeyboardShortcut } from '@repo/commons/models'
	import {
		insertTextAtCursor,
		autoResizeTextarea,
		handleKeyboardShortcuts
	} from '../markdownInputUtils'

	interface MarkdownTextareaProps {
		id: string
		value: string
		placeholder?: string
		readonly?: boolean
		disabled?: boolean
		name?: string
		maxHeight?: string
		minHeight?: string
		showError?: boolean
		class?: string
		onInput?: (value: string) => void
		onBlur?: () => void
		onKeyDown?: (event: KeyboardEvent) => void
		onInsertText?: (before: string, after: string) => void
		allowLineBreaks?: boolean
	}

	let {
		id,
		value = $bindable(''),
		placeholder = 'Start typing...',
		readonly = false,
		disabled = false,
		name,
		maxHeight = '300px',
		minHeight = undefined,
		showError = false,
		class: className = '',
		onInput = () => {},
		onBlur = () => {},
		onKeyDown = () => {},
		onInsertText = () => {},
		allowLineBreaks = true
	}: MarkdownTextareaProps = $props()

	let textareaRef = $state<HTMLTextAreaElement>()
	let isUpdatingFromParent = $state(false)
	export { textareaRef }

	export function insertText(before: string, after: string = '') {
		if (readonly || disabled || !textareaRef) return

		const scrollTop = textareaRef.scrollTop // Preserve scroll position
		isUpdatingFromParent = true // Flag to prevent auto-resize interference

		const result = insertTextAtCursor(value, textareaRef, before, after)
		value = result.newValue
		onInput(value)
		onInsertText(before, after)

		// Preserve scroll
		requestAnimationFrame(() => {
			if (textareaRef) {
				textareaRef.setSelectionRange(result.selectionStart, result.selectionEnd)
				textareaRef.scrollTop = scrollTop // Restore scroll position
				isUpdatingFromParent = false // Reset flag
			}
		})
	}

	// New function to replace selected text and trigger proper input handling
	export function replaceSelectedText(newText: string) {
		if (readonly || disabled || !textareaRef) return

		const start = textareaRef.selectionStart
		const end = textareaRef.selectionEnd
		const scrollTop = textareaRef.scrollTop // Preserve scroll position

		isUpdatingFromParent = true // Flag to prevent auto-resize interference

		const newValue = value.substring(0, start) + newText + value.substring(end)

		// Update the value and trigger input handling
		value = newValue
		onInput(value)

		// Set cursor after the inserted text and restore scroll position
		requestAnimationFrame(() => {
			if (textareaRef) {
				const newPosition = start + newText.length
				textareaRef.focus()
				textareaRef.setSelectionRange(newPosition, newPosition)
				textareaRef.scrollTop = scrollTop // Restore scroll position
				isUpdatingFromParent = false // Reset flag
			}
		})
	}

	function handleInputEvent(event: Event) {
		const target = event.target as HTMLTextAreaElement
		value = target.value
		onInput(value)

		// Only auto-resize if not updating from parent methods
		if (!isUpdatingFromParent) {
			autoResizeTextarea(target, maxHeight)
		}
	}

	function handleKeyDownEvent(event: KeyboardEvent) {
		if (!textareaRef) return

		if (!allowLineBreaks && event.key === 'Enter') {
			event.preventDefault()

			return
		}

		// Default keyboard shortcuts for markdown
		const shortcuts: KeyboardShortcut[] = [
			{
				key: 'b',
				ctrlKey: true,
				action: () => insertText('**', '**')
			},
			{
				key: 'b',
				metaKey: true,
				action: () => insertText('**', '**')
			},
			{
				key: 'i',
				ctrlKey: true,
				action: () => insertText('*', '*')
			},
			{
				key: 'i',
				metaKey: true,
				action: () => insertText('*', '*')
			},
			{
				key: 's',
				ctrlKey: true,
				shiftKey: true,
				action: () => {
					event.preventDefault()
					insertText('~~', '~~')
				}
			},
			{
				key: 's',
				metaKey: true,
				shiftKey: true,
				action: () => {
					event.preventDefault()
					insertText('~~', '~~')
				}
			},
			{
				key: 'k',
				ctrlKey: true,
				action: handleLinkShortcut
			},
			{
				key: 'k',
				metaKey: true,
				action: handleLinkShortcut
			}
		]

		handleKeyboardShortcuts(event, shortcuts)
		onKeyDown(event)
	}

	// This will be handled by the parent component
	function handleLinkShortcut() {
		onKeyDown(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }))
	}

	onMount(() => {
		if (textareaRef) {
			autoResizeTextarea(textareaRef, maxHeight)
		}
	})

	$effect(() => {
		if (textareaRef && value !== undefined && !isUpdatingFromParent) {
			tick().then(() => {
				if (textareaRef && !isUpdatingFromParent) {
					autoResizeTextarea(textareaRef, maxHeight)
				}
			})
		}
	})
</script>

<textarea
	bind:this={textareaRef}
	{id}
	class="textarea-field {className}"
	bind:value
	{placeholder}
	{readonly}
	{disabled}
	{name}
	style="max-height: {maxHeight}{minHeight ? `; min-height: ${minHeight}` : ''}"
	oninput={handleInputEvent}
	onkeydown={handleKeyDownEvent}
	onblur={onBlur}
></textarea>

<style>
	.textarea-field {
		@apply w-full h-full min-h-[100px] p-4 bg-transparent border-0 outline-0;
	}

	.textarea-field::placeholder {
		@apply text-[#001D33B2];
	}

	.textarea-field.invalid {
		@apply border border-red-500;
	}
</style>
